import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Mail, Loader, Trash2 } from "lucide-react";

const AdminEmailLog = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    const isAdmin = localStorage.getItem('shootzu_admin');
    if (!isAdmin) navigate('/szadmin');
  }, [navigate]);

  useEffect(() => {
    fetchLogs(true);
  }, [searchQuery]);

  const fetchLogs = async (reset = false) => {
    try {
      if (reset) { setLoading(true); setCurrentPage(1); }
      else { setLoadingMore(true); }
      const page = reset ? 1 : currentPage;
      const API = process.env.REACT_APP_BACKEND_URL || 'https://shootzu.com';
      const res = await axios.get(`${API}/api/admin/email-logs`, {
        params: { search: searchQuery || undefined, page, limit: 20 }
      });
      if (reset) setLogs(res.data.logs || []);
      else setLogs(prev => [...prev, ...(res.data.logs || [])]);
      setTotalLogs(res.data.total || 0);
      setHasMore(res.data.has_more || false);
      if (!reset) setCurrentPage(prev => prev + 1);
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); setLoadingMore(false); }
  };

  const clearAllLogs = async () => {
    if (!window.confirm('Are you sure you want to clear ALL email logs? This cannot be undone.')) return;
    try {
      setClearing(true);
      const API = process.env.REACT_APP_BACKEND_URL || 'https://shootzu.com';
      await axios.delete(`${API}/api/admin/email-logs`);
      setLogs([]);
      setTotalLogs(0);
      setHasMore(false);
    } catch (err) {
      console.error('Error clearing logs:', err);
      alert('Failed to clear logs');
    }
    finally { setClearing(false); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-full" style={{ background: '#F5F6F9' }}>
      {/* Sticky header */}
      <header className="sz-header flex items-center justify-between gap-[18px]">
        <div className="flex items-center gap-[11px] min-w-0">
          <h1 className="sz-h1 truncate" style={{ margin: 0 }}>Email Log</h1>
          <span
            style={{
              fontFamily: 'Poppins', fontSize: '11.5px', fontWeight: 700,
              color: '#475569', background: '#E2E8F0', padding: '4px 10px', borderRadius: '999px', whiteSpace: 'nowrap'
            }}
          >
            {totalLogs} sent
          </span>
        </div>
        <button
          onClick={clearAllLogs}
          disabled={clearing || totalLogs === 0}
          className="flex items-center gap-[7px] shrink-0"
          style={{
            padding: '10px 16px', border: '1px solid #FECACA', borderRadius: '11px',
            background: '#fff', color: '#DC2626', fontFamily: 'Work Sans', fontSize: '12.5px', fontWeight: 600,
            cursor: (clearing || totalLogs === 0) ? 'not-allowed' : 'pointer',
            opacity: (clearing || totalLogs === 0) ? 0.5 : 1
          }}
        >
          {clearing ? <Loader className="w-[15px] h-[15px] animate-spin" /> : <Trash2 className="w-[15px] h-[15px]" />}
          Clear All
        </button>
      </header>

      <main style={{ padding: '22px 30px 56px', maxWidth: '1200px' }}>
        {/* Search */}
        <div className="relative" style={{ marginBottom: '16px', maxWidth: '420px' }}>
          <Search className="w-4 h-4 absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#A8AEBC' }} />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search subject or recipient…"
            className="sz-input"
            style={{ paddingLeft: '36px' }}
          />
        </div>

        {loading ? (
          <div className="flex justify-center" style={{ padding: '48px 0' }}>
            <Loader className="w-6 h-6 animate-spin" style={{ color: '#572B7C' }} />
          </div>
        ) : (
          <>
            {/* Desktop table — hidden on mobile */}
            <div className="hidden md:block sz-card" style={{ overflow: 'hidden' }}>
              <div
                className="grid items-center"
                style={{
                  gridTemplateColumns: '120px 90px 1.6fr 1.4fr', gap: '12px', padding: '13px 18px',
                  borderBottom: '1px solid #EEF0F4', background: '#FAFBFC',
                  fontSize: '11px', fontWeight: 700, fontFamily: 'Poppins', letterSpacing: '.4px',
                  color: '#8A90A2', textTransform: 'uppercase'
                }}
              >
                <div>Date</div><div>Time</div><div>Subject</div><div>Recipient</div>
              </div>
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="grid items-center"
                  style={{ gridTemplateColumns: '120px 90px 1.6fr 1.4fr', gap: '12px', padding: '11px 18px', borderBottom: '1px solid #F1F3F7' }}
                >
                  <div style={{ fontSize: '12px', color: '#2B3142', fontFamily: 'Poppins' }}>{formatDate(log.sent_at)}</div>
                  <div style={{ fontSize: '12px', color: '#8A90A2', fontFamily: 'Poppins' }}>{formatTime(log.sent_at)}</div>
                  <div style={{ fontSize: '12.5px', color: '#0C1120', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.subject}</div>
                  <div style={{ fontSize: '12px', color: '#2B3142', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.recipient_email}</div>
                </div>
              ))}
              {logs.length === 0 && (
                <div style={{ textAlign: 'center', padding: '44px', color: '#8A90A2', fontSize: '13px' }}>
                  No emails match your search.
                </div>
              )}
            </div>

            {/* Mobile card list — hidden on desktop */}
            <div className="md:hidden flex flex-col gap-[9px]">
              {logs.map((log) => (
                <div key={log.id} className="sz-card" style={{ borderRadius: '12px', padding: '13px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>{log.subject}</div>
                  <div style={{ fontSize: '12px', color: '#2B3142', marginTop: '5px' }}>{log.recipient_email}</div>
                  <div style={{ fontSize: '11px', color: '#8A90A2', marginTop: '3px', fontFamily: 'Poppins' }}>
                    {formatDate(log.sent_at)} · {formatTime(log.sent_at)}
                  </div>
                </div>
              ))}
              {logs.length === 0 && (
                <div style={{ textAlign: 'center', padding: '44px', color: '#8A90A2', fontSize: '13px' }}>
                  No emails match your search.
                </div>
              )}
            </div>

            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: '18px' }}>
                <button
                  onClick={() => fetchLogs(false)}
                  disabled={loadingMore}
                  className="inline-flex items-center justify-center gap-[7px]"
                  style={{
                    padding: '11px 26px', border: '1px solid #E0E3EB', borderRadius: '11px',
                    background: '#fff', fontFamily: 'Work Sans', fontSize: '13px', fontWeight: 600,
                    color: '#2B3142', cursor: loadingMore ? 'not-allowed' : 'pointer', opacity: loadingMore ? 0.6 : 1
                  }}
                >
                  {loadingMore ? <Loader className="w-4 h-4 animate-spin" /> : null}
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminEmailLog;
